import { Link } from 'react-router-dom'
import {
  NodeAvatar,
  NodeAssociations,
  NodeUserCount,
  NodePropertyString,
  NodeName,
  NodePropertyBadge,
  NodeDate,
  NodeProgress,
  NodeUsers,
  NodeRelationshipCount,
  NodeFileTypeIcon,
} from '@/components/nodes'
import { Badge } from '@/components/ui/badge'
import {
  Boxes,
  ClipboardList,
  CheckSquare,
  FileText,
  Users,
  User as UserIcon,
  Mail,
} from 'lucide-react'
import type { Project, Task, Message, Document, Workspace } from '@/types'

// Union type for all node types
export type NodeItem = Project | Task | Message | Document | Workspace

// Context types
export type NodeContext = 'project' | 'task' | 'message' | 'document' | 'workspace'

/**
 * Standard layout sections for consistent positioning
 */
export type NodeLayoutSection = 'left' | 'center' | 'right' | 'bottom'

/**
 * Standard spacing tokens for consistent gaps
 */
export type NodeSpacing = 'xs' | 'sm' | 'md' | 'lg'

/**
 * Standard text size tokens
 */
export type NodeTextSize = 'xs' | 'sm' | 'base'

/**
 * Standard avatar size tokens
 */
export type NodeAvatarSize = 'xs' | 'sm' | 'md' | 'lg'

/**
 * Base configuration interface with standardized options
 */
export interface BaseNodeConfig {
  className?: string
  // Layout options
  showDescription?: boolean
  showCreator?: boolean
  showDate?: boolean
  // Common spacing
  spacing?: NodeSpacing
  // Common text sizing
  textSize?: NodeTextSize
  textSizeName?: NodeTextSize
  textSizeDescription?: NodeTextSize
  // Common hover modes
  hoverMode?: boolean
}

// Context-specific configuration interfaces - now standardized
interface ProjectNodeConfig extends BaseNodeConfig {
  showStatus?: boolean
  showPriority?: boolean
  showHorizon?: boolean
  showWorkspace?: boolean
  showUserCount?: boolean
  avatarSize?: NodeAvatarSize
  avatarPosition?: NodeLayoutSection
}

interface TaskNodeConfig extends BaseNodeConfig {
  showStatus?: boolean
  showPriority?: boolean
  showHorizon?: boolean
  showType?: boolean
  showWorkspace?: boolean
  showProject?: boolean
  showAssignee?: boolean
  showDueDate?: boolean
  showSubtasks?: boolean
  statusSize?: 'sm' | 'md'
  assigneePosition?: NodeLayoutSection
  dueDatePosition?: NodeLayoutSection
}

export interface MessageNodeConfig extends BaseNodeConfig {
  showReplyForm?: boolean
  showRootReplyForm?: boolean
  showTitle?: boolean
  maxLines?: number
  showWorkspace?: boolean
  showTasks?: boolean
  showDocuments?: boolean
  showProjects?: boolean
  associationsMaxVisible?: number
  associationsDisplayMode?: 'badge' | 'text' | 'card'
  showRelationshipCounts?: boolean
  showReadStatus?: boolean
  showMessageType?: boolean
  showReplyCount?: boolean
  showNestedReplies?: boolean
  currentUserId?: string
  onClick?: (message: Message) => void
}

interface DocumentNodeConfig extends BaseNodeConfig {
  showType?: boolean
  showTypeIcon?: boolean
  showWorkspace?: boolean
  showCreatorName?: boolean
  showSharedCount?: boolean
  avatarSize?: NodeAvatarSize
  typeIconPosition?: NodeLayoutSection
  creatorPosition?: NodeLayoutSection
}

interface WorkspaceNodeConfig extends BaseNodeConfig {
  showUserCount?: boolean
  showCreator?: boolean
  showDescription?: boolean
  showRelationshipCounts?: boolean
  avatarSize?: NodeAvatarSize
  avatarPosition?: NodeLayoutSection
}

// Union type for all configuration types
export type NodeConfig =
  | ProjectNodeConfig
  | TaskNodeConfig
  | MessageNodeConfig
  | DocumentNodeConfig
  | WorkspaceNodeConfig

/**
 * Design tokens for consistent spacing
 */
const spacingTokens: Record<NodeSpacing, string> = {
  xs: 'gap-1',
  sm: 'gap-2',
  md: 'gap-3',
  lg: 'gap-4',
}

/**
 * Design tokens for consistent avatar sizing
 */
const avatarSizeTokens: Record<NodeAvatarSize, 'xs' | 'sm' | 'md' | 'lg'> = {
  xs: 'xs',
  sm: 'sm',
  md: 'md',
  lg: 'lg',
}

/**
 * Default configuration values for each context
 */
const defaultConfig: Record<NodeContext, Partial<NodeConfig>> = {
  project: {
    spacing: 'md',
    textSize: 'base',
    textSizeName: 'base',
    textSizeDescription: 'sm',
    showDescription: true,
    showCreator: true,
    showWorkspace: true,
    showStatus: true,
    showPriority: true,
    showHorizon: true,
    showUserCount: true,
    avatarSize: 'md',
    avatarPosition: 'right',
  },
  task: {
    spacing: 'md',
    textSize: 'base',
    textSizeName: 'base',
    textSizeDescription: 'sm',
    showDescription: true,
    showWorkspace: true,
    showProject: true,
    showAssignee: true,
    showDueDate: true,
    showSubtasks: true,
    showStatus: true,
    showPriority: true,
    showHorizon: true,
    showType: true,
    assigneePosition: 'right',
    dueDatePosition: 'right',
  },
  message: {
    spacing: 'md',
    textSize: 'base',
    textSizeName: 'base',
    textSizeDescription: 'sm',
    showDescription: true,
    showTitle: true,
    showCreator: true,
    showDate: true,
    showWorkspace: true,
    showTasks: true,
    showDocuments: true,
    showProjects: true,
    showReplyCount: true,
    associationsMaxVisible: 3,
    associationsDisplayMode: 'badge',
  },
  document: {
    spacing: 'md',
    textSize: 'sm',
    textSizeName: 'base',
    textSizeDescription: 'sm',
    showDescription: true,
    showType: true,
    showTypeIcon: true,
    showWorkspace: true,
    showCreator: true,
    showDate: true,
    showCreatorName: false,
    showSharedCount: true,
    avatarSize: 'md',
    typeIconPosition: 'left',
    creatorPosition: 'right',
  },
  workspace: {
    spacing: 'md',
    textSize: 'base',
    textSizeName: 'base',
    textSizeDescription: 'sm',
    showDescription: true,
    showCreator: true,
    showUserCount: true,
    avatarSize: 'md',
    avatarPosition: 'right',
  },
}

/**
 * Unified base layout component that all contexts use
 */
interface NodeLayoutProps {
  config: NodeConfig
  children: React.ReactNode
  className?: string
}

function NodeLayout({ config, children, className }: NodeLayoutProps) {
  const spacing = spacingTokens[config.spacing || 'md']
  const baseClasses = `flex items-center ${spacing} p-3 rounded-lg border hover:bg-accent transition-colors ${config.className || ''} ${className || ''}`

  return <div className={baseClasses}>{children}</div>
}

/**
 * Layout section components for consistent positioning
 */
interface NodeLayoutSectionProps {
  children: React.ReactNode
  position: NodeLayoutSection
  className?: string
}

function NodeLayoutSection({ children, position, className }: NodeLayoutSectionProps) {
  const positionClasses = {
    left: 'flex-shrink-0',
    center: 'flex-1 min-w-0',
    right: 'flex-shrink-0',
    bottom: 'flex flex-col gap-1 w-full',
  }

  return <div className={`${positionClasses[position]} ${className || ''}`}>{children}</div>
}

/**
 * Badge row component for consistent badge layout
 */
interface NodeBadgeRowProps {
  children: React.ReactNode
  spacing?: NodeSpacing
  className?: string
}

function NodeBadgeRow({ children, spacing = 'sm', className }: NodeBadgeRowProps) {
  const gapClass = spacingTokens[spacing]
  return <div className={`flex items-center ${gapClass} ${className || ''}`}>{children}</div>
}

/**
 * Metadata row component for consistent metadata layout
 */
interface NodeMetadataRowProps {
  children: React.ReactNode
  spacing?: NodeSpacing
  className?: string
}

function NodeMetadataRow({ children, spacing = 'sm', className }: NodeMetadataRowProps) {
  const gapClass = spacingTokens[spacing]
  return (
    <div
      className={`flex items-center mt-2 ${gapClass} text-xs text-muted-foreground ${className || ''}`}
    >
      {children}
    </div>
  )
}

// Main props interface for NodeListItem
export interface NodeListItemProps<T extends NodeItem = NodeItem> {
  /**
   * The node item to display
   */
  item: T
  /**
   * Context type - determines which rendering logic to use
   */
  context: NodeContext
  /**
   * Configuration object for the specific context
   */
  config?: NodeConfig
}

/**
 * Unified list item component that can display any type of node (project, task, message, document)
 * with context-specific configuration and rendering using standardized design tokens.
 */
export function NodeListItem<T extends NodeItem>({
  item,
  context,
  config = {},
}: NodeListItemProps<T>) {
  // Merge with default configuration for the context
  const mergedConfig = { ...defaultConfig[context], ...config }

  // Determine if this should be wrapped in a Link
  const shouldLink = context !== 'message' || !('onClick' in mergedConfig && mergedConfig.onClick)
  const linkPath = getLinkPath(item, context)

  // Get the content based on context using unified configuration
  const content = getContextContent(item, context, mergedConfig)

  // Handle click for messages
  const handleClick = () => {
    if (context === 'message' && 'onClick' in mergedConfig && mergedConfig.onClick) {
      mergedConfig.onClick!(item as Message)
    }
  }

  if (shouldLink && linkPath) {
    return (
      <Link to={linkPath}>
        <div onClick={handleClick}>{content}</div>
      </Link>
    )
  }

  return <div onClick={handleClick}>{content}</div>
}

/**
 * Get the appropriate link path for a node based on its context
 */
function getLinkPath(item: NodeItem, context: NodeContext): string | null {
  switch (context) {
    case 'project':
      return `/projects/${item.id}`
    case 'task':
      return `/tasks/${item.id}`
    case 'message':
      return `/messages/${item.id}`
    case 'document':
      return `/documents/${item.id}`
    case 'workspace':
      return `/workspaces/${item.id}`
    default:
      return null
  }
}

/**
 * Get the content JSX for a specific context
 */
function getContextContent(item: NodeItem, context: NodeContext, config: NodeConfig): JSX.Element {
  switch (context) {
    case 'project':
      return renderProjectContent(item as Project, config as ProjectNodeConfig)
    case 'task':
      return renderTaskContent(item as Task, config as TaskNodeConfig)
    case 'message':
      return renderMessageContent(item as Message, config as MessageNodeConfig)
    case 'document':
      return renderDocumentContent(item as Document, config as DocumentNodeConfig)
    case 'workspace':
      return renderWorkspaceContent(item as Workspace, config as WorkspaceNodeConfig)
    default:
      return <div>Unknown context</div>
  }
}

/**
 * Render project-specific content using unified layout system
 */
function renderProjectContent(project: Project, config: ProjectNodeConfig): JSX.Element {
  return (
    <NodeLayout config={config}>
      {/* Main content area */}
      <NodeLayoutSection position="center">
        <div className="flex flex-col gap-1">
          {/* Name and badges row */}
          <NodeBadgeRow>
            <NodeName
              name={project.name}
              nodeId={project.id}
              linkBasePath="/projects"
              textSize={config.textSizeName || config.textSize || 'base'}
            />
            {config.showStatus && (
              <NodePropertyBadge
                type="status"
                value={project.status}
                iconSize="sm"
                label=""
                hoverMode={config.hoverMode}
              />
            )}
            {config.showPriority && (
              <NodePropertyBadge
                type="priority"
                value={project.priority}
                iconSize="sm"
                label=""
                hoverMode={config.hoverMode}
              />
            )}
            {config.showHorizon && (
              <NodePropertyBadge
                type="horizon"
                value={project.horizon}
                iconSize="sm"
                label=""
                hoverMode={config.hoverMode}
              />
            )}
          </NodeBadgeRow>

          {/* Description */}
          {config.showDescription && (
            <NodePropertyString
              description={project.description}
              displayMode="single-line"
              textSize={config.textSizeDescription || config.textSize || 'xs'}
            />
          )}

          {/* Metadata row */}
          {(config.showWorkspace || config.showUserCount) && (
            <NodeMetadataRow>
              {config.showWorkspace && project.workspace && (
                <NodeAssociations
                  nodes={[project.workspace]}
                  nodeType="workspaces"
                  showViewAll={false}
                  displayMode="text"
                  label=""
                  linkColor="muted"
                  listIcon={true}
                />
              )}
              {config.showUserCount && <NodeUserCount users={project.users} iconSize="sm" />}
            </NodeMetadataRow>
          )}
        </div>
      </NodeLayoutSection>

      {/* Avatar positioned based on config */}
      {config.showCreator && project.creator && (
        <NodeLayoutSection position={config.avatarPosition || 'right'}>
          <NodeAvatar node={project.creator} size={avatarSizeTokens[config.avatarSize || 'md']} />
        </NodeLayoutSection>
      )}
    </NodeLayout>
  )
}

/**
 * Render task-specific content using unified layout system
 */
function renderTaskContent(task: Task, config: TaskNodeConfig): JSX.Element {
  const totalSubtasks = task.subtasks?.length || 0

  return (
    <NodeLayout config={config}>
      {/* Main content area */}
      <NodeLayoutSection position="center">
        <div className="flex flex-col gap-1">
          {/* Name and badges row */}
          <NodeBadgeRow>
            <NodeName
              name={task.name}
              nodeId={task.id}
              linkBasePath="/tasks"
              textSize={config.textSizeName || config.textSize || 'base'}
            />
            {config.showStatus && (
              <NodePropertyBadge
                type="status"
                value={task.status}
                iconSize={config.statusSize || 'sm'}
                label=""
                hoverMode={config.hoverMode}
              />
            )}
            {config.showPriority && (
              <NodePropertyBadge
                type="priority"
                value={task.priority}
                iconSize="sm"
                label=""
                hoverMode={config.hoverMode}
              />
            )}
            {config.showHorizon && (
              <NodePropertyBadge
                type="horizon"
                value={task.horizon}
                iconSize="sm"
                label=""
                hoverMode={config.hoverMode}
              />
            )}
            {config.showType && (
              <NodePropertyBadge
                type="taskType"
                value={task.type}
                label=""
                hoverMode={config.hoverMode}
              />
            )}
          </NodeBadgeRow>

          {/* Description */}
          {config.showDescription && (
            <NodePropertyString
              description={task.description}
              displayMode="single-line"
              textSize={config.textSizeDescription || config.textSize || 'xs'}
            />
          )}

          {/* Metadata row */}
          {(config.showWorkspace || config.showProject || config.showSubtasks) && (
            <NodeMetadataRow>
              {config.showWorkspace && task.workspace && (
                <NodeAssociations
                  nodes={[task.workspace]}
                  nodeType="workspaces"
                  maxVisible={1}
                  displayMode="text"
                  showViewAll={false}
                  label=""
                  linkColor="muted"
                  listIcon={true}
                />
              )}
              {config.showProject && task.projects && task.projects.length > 0 && (
                <NodeAssociations
                  nodes={task.projects}
                  nodeType="projects"
                  maxVisible={1}
                  displayMode="text"
                  showViewAll={false}
                  label=""
                  linkColor="muted"
                  listIcon={true}
                />
              )}
              {config.showSubtasks && totalSubtasks > 0 && (
                <NodeProgress items={task.subtasks || []} label="" className="text-xs" />
              )}
            </NodeMetadataRow>
          )}
        </div>
      </NodeLayoutSection>

      {/* Right side items positioned based on config */}
      <NodeLayoutSection position="right">
        <div className="flex items-center gap-2">
          {config.showAssignee && (
            <NodeUsers
              users={task.assignee ? [task.assignee] : []}
              maxVisible={1}
              avatarSize="sm"
              showViewAll={false}
              emptyState={
                <div className="text-muted-foreground">
                  <span className="text-sm hidden md:block">Unassigned</span>
                </div>
              }
            />
          )}

          {config.showDueDate && task.dueDate && (
            <NodeDate dueDate={task.dueDate} label="" format="short" size="sm" />
          )}
        </div>
      </NodeLayoutSection>
    </NodeLayout>
  )
}

/**
 * Render message-specific content using unified layout system
 */
function renderMessageContent(message: Message, config: MessageNodeConfig): JSX.Element {
  // Determine if message has been read by current user
  const isRead = config.currentUserId
    ? message.readBy.some((read) => read.userId === config.currentUserId)
    : false

  return (
    <NodeLayout config={config}>
      {/* Main content area with title and description */}
      <NodeLayoutSection position="center">
        <div className="flex flex-col gap-1">
          {/* Title and badges in top row */}
          {(config.showTitle || config.showMessageType || config.showReadStatus) && (
            <NodeBadgeRow>
              {config.showTitle && (
                <NodeName
                  name={message.name}
                  textSize={config.textSizeName || config.textSize || 'base'}
                  truncate
                />
              )}

              {/* Message type badges */}
              {config.showMessageType && (
                <>
                  {message.directMessage ? (
                    <Badge variant="secondary" className="gap-1">
                      <UserIcon className="h-3 w-3" />
                      Direct
                    </Badge>
                  ) : (
                    <Badge variant="secondary" className="gap-1">
                      <Users className="h-3 w-3" />
                      Group
                    </Badge>
                  )}
                </>
              )}

              {/* New badge for unread messages */}
              {config.showReadStatus && !isRead && (
                <Badge variant="default" className="gap-1">
                  <Mail className="h-3 w-3" />
                  New
                </Badge>
              )}
            </NodeBadgeRow>
          )}

          {/* Description */}
          {config.showDescription && (
            <NodePropertyString
              description={message.description}
              textSize={config.textSizeDescription || config.textSize || 'xs'}
              displayMode="multi-line"
              maxLines={config.maxLines}
              className={!config.showTitle ? 'mt-3' : ''}
            />
          )}

          {/* Metadata row */}
          {(config.showCreator ||
            config.showDate ||
            config.showRelationshipCounts ||
            config.showReplyCount) && (
            <NodeMetadataRow>
              {config.showCreator && (
                <NodeUsers
                  users={[message.creator]}
                  maxVisible={1}
                  avatarSize="sm"
                  showViewAll={false}
                />
              )}
              {config.showDate && (
                <NodeDate
                  dueDate={message.createdAt}
                  label=""
                  size="sm"
                  format="medium"
                  showOverdue={false}
                />
              )}
              {config.showRelationshipCounts && (
                <NodeRelationshipCount
                  node={message}
                  relationships={['tasks', 'documents', 'projects', 'messages']}
                  className="text-xs"
                />
              )}
              {config.showReplyCount && message.messages && message.messages.length > 0 && (
                <span>{message.messages.length} replies</span>
              )}
            </NodeMetadataRow>
          )}

          {/* Associated Nodes section */}
          {(config.showWorkspace ||
            config.showProjects ||
            config.showTasks ||
            config.showDocuments) && (
            <NodeLayoutSection position="bottom" className="mt-1">
              {/* Workspace */}
              {message.workspace && config.showWorkspace && (
                <NodeAssociations
                  nodes={[message.workspace]}
                  nodeType="workspaces"
                  maxVisible={1}
                  showViewAll={false}
                  icon={Boxes}
                  label="Workspace:"
                  className="text-xs"
                />
              )}

              {/* Projects */}
              {message.projects.length > 0 && config.showProjects && (
                <NodeAssociations
                  nodes={message.projects}
                  nodeType="projects"
                  maxVisible={config.associationsMaxVisible}
                  showViewAll={false}
                  icon={ClipboardList}
                  className="text-xs"
                />
              )}

              {/* Tasks */}
              {message.tasks.length > 0 && config.showTasks && (
                <NodeAssociations
                  nodes={message.tasks}
                  nodeType="tasks"
                  maxVisible={config.associationsMaxVisible}
                  showViewAll={false}
                  icon={CheckSquare}
                  className="text-xs"
                />
              )}

              {/* Documents */}
              {message.documents.length > 0 && config.showDocuments && (
                <NodeAssociations
                  nodes={message.documents}
                  nodeType="documents"
                  maxVisible={config.associationsMaxVisible}
                  showViewAll={false}
                  displayMode={config.associationsDisplayMode}
                  icon={FileText}
                  className="text-xs"
                />
              )}
            </NodeLayoutSection>
          )}
        </div>
      </NodeLayoutSection>
    </NodeLayout>
  )
}

/**
 * Render document-specific content using unified layout system
 */
function renderDocumentContent(document: Document, config: DocumentNodeConfig): JSX.Element {
  const workspaceName = document.workspace?.name || 'Unknown Workspace'
  const sharedCount = document.users?.length || 0

  return (
    <NodeLayout config={config}>
      {/* Type icon positioned based on config */}
      {config.showTypeIcon && (
        <NodeLayoutSection position={config.typeIconPosition || 'left'}>
          <NodeFileTypeIcon type={document.type} className="h-5 w-5 mt-0.5" />
        </NodeLayoutSection>
      )}

      {/* Main content area */}
      <NodeLayoutSection position="center">
        <div className="flex flex-col gap-1">
          {/* Name and type badge row */}
          <NodeBadgeRow>
            <NodeName
              name={document.name}
              textSize={config.textSizeName || config.textSize || 'base'}
              truncate
            />
            {config.showType && (
              <Badge variant="secondary" className="text-xs">
                {document.type}
              </Badge>
            )}
          </NodeBadgeRow>

          {/* Description */}
          {config.showDescription && (
            <NodePropertyString
              description={document.description}
              textSize={config.textSizeDescription || config.textSize || 'xs'}
              displayMode="multi-line"
              maxLines={2}
              className="text-muted-foreground"
            />
          )}

          {/* Metadata row */}
          {(config.showWorkspace || config.showSharedCount) && (
            <NodeMetadataRow>
              {config.showWorkspace && <span className="truncate">{workspaceName}</span>}
              {config.showSharedCount && sharedCount > 1 && (
                <span>Shared with {sharedCount - 1}</span>
              )}
            </NodeMetadataRow>
          )}
        </div>
      </NodeLayoutSection>

      {/* Creator and date positioned based on config */}
      <NodeLayoutSection position={config.creatorPosition || 'right'}>
        <div className="flex items-center gap-2">
          {config.showCreator && document.creator && (
            <div className="flex items-center gap-2">
              <NodeAvatar
                node={document.creator}
                size={avatarSizeTokens[config.avatarSize || 'md']}
              />
              {config.showCreatorName && (
                <span className="text-sm hidden md:block">{document.creator.name}</span>
              )}
            </div>
          )}

          {config.showDate && (
            <NodeDate
              dueDate={document.updatedAt}
              label=""
              size="sm"
              format="medium"
              showOverdue={false}
              className="min-w-[80px]"
            />
          )}
        </div>
      </NodeLayoutSection>
    </NodeLayout>
  )
}

/**
 * Render workspace-specific content using unified layout system
 */
function renderWorkspaceContent(workspace: Workspace, config: WorkspaceNodeConfig): JSX.Element {
  return (
    <NodeLayout config={config}>
      {/* Main content area */}
      <NodeLayoutSection position="center">
        <div className="flex flex-col gap-1">
          {/* Name and badges row */}
          <NodeBadgeRow>
            <NodeName
              name={workspace.name}
              nodeId={workspace.id}
              linkBasePath="/workspaces"
              textSize={config.textSizeName || config.textSize || 'base'}
            />
          </NodeBadgeRow>

          {/* Description (email in this case) */}
          {config.showDescription && (
            <NodePropertyString
              description={workspace.email}
              displayMode="single-line"
              textSize={config.textSizeDescription || config.textSize || 'xs'}
            />
          )}

          {/* Metadata row */}
          {config.showUserCount && (
            <NodeMetadataRow>
              <NodeUserCount users={workspace.users} iconSize="sm" />
            </NodeMetadataRow>
          )}

          {/* Relationship counts */}
          {config.showRelationshipCounts && (
            <NodeMetadataRow>
              <NodeRelationshipCount
                node={workspace}
                relationships={['users', 'projects', 'tasks', 'messages', 'documents']}
                className="text-xs"
              />
            </NodeMetadataRow>
          )}
        </div>
      </NodeLayoutSection>

      {/* Avatar positioned based on config */}
      {config.showCreator && workspace.creator && (
        <NodeLayoutSection position={config.avatarPosition || 'right'}>
          <NodeAvatar node={workspace.creator} size={avatarSizeTokens[config.avatarSize || 'md']} />
        </NodeLayoutSection>
      )}
    </NodeLayout>
  )
}
